// Pure helpers for rendering a human-readable preview of the SQL / session
// calls a form will execute, shown behind the confirm modal's "Show details"
// toggle. Display-only — the backend builds and *binds* the real statements;
// these mirror that logic so the user can see exactly what will run.

// Backtick-quote a MySQL identifier (escaping embedded backticks).
export const ident = (s) => '`' + String(s ?? '').replace(/`/g, '``') + '`';

// Render a value as a SQL literal for display: NULL for null/undefined, bare
// for numbers, single-quoted otherwise. Sessions run without
// NO_BACKSLASH_ESCAPES, so a backslash is doubled as well as a quote — else
// C:\new reads back as C:<newline>ew and a trailing \ swallows the closing quote.
export const sqlLiteral = (v) =>
  v == null ? 'NULL'
    : typeof v === 'number' ? String(v)
      : `'${String(v).replace(/\\/g, '\\\\').replace(/'/g, "''")}'`;

// Render the UPDATE a single-row grid edit WOULD run, for a live preview before
// it's sent. Mirrors the backend's renderUpdateForLog (querysession.go): SET
// columns sorted for stable display, values inlined as literals, PK in the
// WHERE. Display-only — the real statement is parameter-bound. Returns '' when
// nothing changed (so the caller can hide the preview).
export function buildUpdatePreview({ database, table, pkColumns, pkValues, setColumns }) {
  const cols = Object.keys(setColumns || {}).sort();
  if (cols.length === 0) return '';
  const sets = cols.map((c) => `${ident(c)} = ${sqlLiteral(setColumns[c])}`).join(', ');
  const where = (pkColumns || [])
    .map((c, i) => `${ident(c)} = ${sqlLiteral(pkValues[i])}`)
    .join(' AND ');
  return `UPDATE ${ident(database)}.${ident(table)} SET ${sets}${where ? ` WHERE ${where}` : ''}`;
}

// Render the INSERT a new-row draft would run, for a live preview before it's
// sent. Mirrors the backend renderInsertForLog (columns sorted, values inlined).
// Returns '' when there are no columns to insert (so the caller can hide it).
export function buildInsertPreview({ database, table, columns }) {
  const cols = Object.keys(columns || {}).sort();
  if (cols.length === 0) return '';
  const names = cols.map(ident).join(', ');
  const vals  = cols.map((c) => sqlLiteral(columns[c])).join(', ');
  return `INSERT INTO ${ident(database)}.${ident(table)} (${names}) VALUES (${vals})`;
}

// snake_case → TitleCase, matching the backend titleCase() used for @v var
// names (e.g. "no_jual" → "NoJual", so the var is @vNoJual).
export const titleCase = (s) =>
  String(s ?? '')
    .split(/[_\-\s]+/)
    .map((p) => (p ? p[0].toUpperCase() + p.slice(1).toLowerCase() : ''))
    .join('');
